// merchantService — client for the gated /merchant portal (Plan 26-06).
//
// Mirrors fbGroupReviewService.ts: axios + getApiBaseUrl() + getHeaders() + error-unwrap.
// The base URL already includes `/api`, so BASE must NOT prefix `/api`.
//
// SECURITY NOTE (T-26-13): the client-side gate (MerchantRoute) is UX only. Every endpoint
// below is independently enforced server-side by merchantAuth (Plan 02) — a bypassed client
// gate exposes no data.

import axios from "axios";
import { getApiBaseUrl, getHeaders } from "./apiClient";
import { BackendApiResponse } from "./adminService";
import type { Review, ReviewStats } from "@/types/review";
import { ADMIN_WRITE_CANCELLED, confirmAdminWrite } from "@/lib/adminWriteGate";

// Base already includes `/api` (see apiClient.getApiBaseUrl) — never prefix `/api` here.
const BASE = "/merchant";

// ─── Types ───

export type MerchantDashboard = {
  averageRating: number;
  reviewCount: number;
  recentReviews: Review[];
};

// The live Store plus its current open pending-edit changeset (loosely typed here;
// the edit form in Plan 08 owns the precise field-level shape).
export type MerchantStoreResponse = {
  store: Record<string, unknown>;
  pendingEdit: MerchantPendingEdit | null;
};

export type MerchantPendingEdit = {
  _id: string;
  storeId: string;
  status: string;
  changes?: Record<string, unknown>;
  [key: string]: unknown;
} | null;

export type MerchantUpdateResult = {
  instantApplied?: string[];
  queued?: string[];
  [key: string]: unknown;
};

// The merchant reviews surface (Plan 05 GET /merchant/reviews): the store's reviews (merchant
// replies already projected into each review's comments as isMerchantReply=true) plus a
// per-review report-status map (D-13 — status TEXT only, never reasons/notes).
export type MerchantReviewsResponse = {
  reviews: Review[];
  pagination?: Record<string, unknown>;
  stats?: ReviewStats;
  // reviewId → status text ("قيد المراجعة" | "تمت المراجعة"). Absent key = never reported.
  reportStatuses: Record<string, string>;
};

// The four-value report reason enum (matches backend reportReasonMap). Arabic labels live in
// the reviews UI (MerchantReviews) — the client sends the enum key.
export type MerchantReportReason = "fake" | "abusive" | "unrelated" | "other";

// ─── Custom Quick Details fields (quick-260813-ttw) ───

// A live catalog definition (seeded or admin-approved) the merchant can pick from.
export type CatalogField = {
  _id: string;
  key: string;
  labelAr: string;
  status: "seeded" | "pending" | "approved" | "rejected";
};

// A published row on the store (label denormalized from the catalog definition).
export type StoreCustomField = { key: string; label: string; value: string };

// The merchant's own pending field-type proposal — value held server-side until approval.
export type FieldProposal = {
  _id: string;
  key: string;
  labelAr: string;
  proposerValue?: string;
};

export type MerchantCustomFieldsResponse = {
  catalog: CatalogField[];
  values: StoreCustomField[];
  pendingProposals: FieldProposal[];
};

export type SaveCustomFieldsResult = {
  published: string[];
  proposed: string[];
};

// One save entry: `key` selects a live catalog field; `label` proposes a new field type.
export type CustomFieldInput = { key?: string; label?: string; value: string };

export type MyClaimStatus = "pending" | "approved" | "rejected" | "revoked";

export type MyClaim = {
  id: string;
  storeId: string;
  store?: {
    name: string;
    slug?: string;
    logoUrl?: string;
    rating?: number;
    totalReviews?: number;
  };
  status: MyClaimStatus;
  evidence?: {
    phone?: string;
    pageUrl?: string;
    note?: string;
    proofImageUrl?: string;
  };
  rejectionReason?: string;
  createdAt?: string;
};

// ─── API functions ───
//
// Every /merchant call accepts an optional storeId. merchantAuth scopes the claim lookup to
// ?storeId= when present (multi-store merchants switch stores client-side); when absent the
// server falls back to any approved claim (single-store behavior, backward compatible).

const storeParams = (storeId?: string) => (storeId ? { storeId } : undefined);

/** GET /api/merchant/dashboard — read-only aggregates (MERCH-09). */
export const getDashboard = async (
  storeId?: string
): Promise<BackendApiResponse<MerchantDashboard>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/dashboard`, {
      headers: getHeaders(),
      params: storeParams(storeId),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر تحميل بيانات لوحة المتجر." };
  }
};

/** GET /api/merchant/store — live Store + open pending-edit changeset. */
export const getStore = async (
  storeId?: string
): Promise<BackendApiResponse<MerchantStoreResponse>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/store`, {
      headers: getHeaders(),
      params: storeParams(storeId),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر تحميل بيانات المتجر." };
  }
};

/** PUT /api/merchant/store — split-apply a merchant edit (flat dotted `changes` map). */
export const updateStore = async (
  changes: Record<string, unknown>,
  storeId?: string
): Promise<BackendApiResponse<MerchantUpdateResult>> => {
  try {
    if (!(await confirmAdminWrite("تعديل بيانات المتجر"))) return { ok: false, error: ADMIN_WRITE_CANCELLED };
    const response = await axios.put(
      `${getApiBaseUrl()}${BASE}/store`,
      { changes },
      { headers: getHeaders(), params: storeParams(storeId) }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر حفظ التغييرات. حاول مرة أخرى." };
  }
};

/** GET /api/merchant/custom-fields — catalog + current values + own pending proposals. */
export const getCustomFields = async (
  storeId?: string
): Promise<BackendApiResponse<MerchantCustomFieldsResponse>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/custom-fields`, {
      headers: getHeaders(),
      params: storeParams(storeId),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر تحميل المعلومات الإضافية." };
  }
};

/**
 * PUT /api/merchant/store/custom-fields — whole-array save. The server decides publish vs
 * propose per entry (live catalog → instant, new label → pending review).
 */
export const saveCustomFields = async (
  fields: CustomFieldInput[],
  storeId?: string
): Promise<BackendApiResponse<SaveCustomFieldsResult>> => {
  try {
    if (!(await confirmAdminWrite("حفظ المعلومات الإضافية"))) return { ok: false, error: ADMIN_WRITE_CANCELLED };
    const response = await axios.put(
      `${getApiBaseUrl()}${BASE}/store/custom-fields`,
      { fields },
      { headers: getHeaders(), params: storeParams(storeId) }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر حفظ المعلومات الإضافية. حاول مرة أخرى." };
  }
};

/** GET /api/merchant/pending-edit — the current open changeset (or null). */
export const getPendingEdit = async (
  storeId?: string
): Promise<BackendApiResponse<MerchantPendingEdit>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/pending-edit`, {
      headers: getHeaders(),
      params: storeParams(storeId),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر تحميل التغييرات المعلّقة." };
  }
};

/** POST /api/merchant/pending-edit/withdraw — withdraw the open changeset before review. */
export const withdrawPendingEdit = async (
  storeId?: string
): Promise<BackendApiResponse<unknown>> => {
  try {
    if (!(await confirmAdminWrite("سحب التعديلات المعلقة"))) return { ok: false, error: ADMIN_WRITE_CANCELLED };
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/pending-edit/withdraw`,
      {},
      { headers: getHeaders(), params: storeParams(storeId) }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر سحب التغييرات المعلّقة." };
  }
};

/** GET /api/merchant/reviews — the store's reviews + per-review report-status pills (D-13).
 * Optional `page`/`limit` drive the backend pagination (used by the widget editor's curation list
 * to load ALL reviews page-by-page); omitted → the backend's first-page default (unchanged). */
export const getReviews = async (
  storeId?: string,
  params?: { page?: number; limit?: number }
): Promise<BackendApiResponse<MerchantReviewsResponse>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/reviews`, {
      headers: getHeaders(),
      params: { ...storeParams(storeId), ...(params || {}) },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر تحميل التقييمات." };
  }
};

/**
 * POST /api/merchant/reviews/:reviewId/reply — post the single public merchant reply
 * (one-per-review, MERCH-07/D-10). Sanitizer / already-replied failures surface as a 400 whose
 * `error` already carries the exact UI-SPEC Arabic copy — passed straight through here.
 */
export const replyToReview = async (
  reviewId: string,
  text: string,
  storeId?: string
): Promise<BackendApiResponse<unknown>> => {
  try {
    if (!(await confirmAdminWrite("الرد على تقييم"))) return { ok: false, error: ADMIN_WRITE_CANCELLED };
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/reviews/${reviewId}/reply`,
      { text },
      { headers: getHeaders(), params: storeParams(storeId) }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر إرسال الرد. حاول مرة أخرى." };
  }
};

/**
 * POST /api/merchant/reviews/:reviewId/report — queue a review report (MERCH-08). The review
 * stays visible; only an admin 'actioned' resolution ever removes it.
 */
export const reportReview = async (
  reviewId: string,
  { reason, note }: { reason: MerchantReportReason; note?: string },
  storeId?: string
): Promise<BackendApiResponse<unknown>> => {
  try {
    if (!(await confirmAdminWrite("الإبلاغ عن تقييم"))) return { ok: false, error: ADMIN_WRITE_CANCELLED };
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/reviews/${reviewId}/report`,
      { reason, note },
      { headers: getHeaders(), params: storeParams(storeId) }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر إرسال البلاغ. حاول مرة أخرى." };
  }
};

/**
 * POST /api/merchant/store/logo — upload a proposed logo file. Lands in the ImageKit
 * merchant-pending/<storeId> folder; the returned URL is then queued through the normal
 * identity-tier pending-edit flow (logoUrl) and only reaches the live store after approval.
 */
export const uploadLogo = async (
  file: File,
  storeId?: string
): Promise<BackendApiResponse<{ url: string }>> => {
  try {
    if (!(await confirmAdminWrite("رفع شعار المتجر"))) return { ok: false, error: ADMIN_WRITE_CANCELLED };
    const formData = new FormData();
    formData.append("logo", file);
    const response = await axios.post(`${getApiBaseUrl()}${BASE}/store/logo`, formData, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      params: storeParams(storeId),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر رفع الصورة. حاول مرة أخرى." };
  }
};

// The single resolved candidate returned by the claim-scoped merchant resolver. Mirrors the
// backend ResolvedBranchCandidate (storeBranchService). `branch` is absent when confidence is
// 'failed'; `reason` carries the server's explanation on a failure or a low-confidence match.
// NOTE: no rating / review-count / timestamp keys exist here — branch diagnostics are admin-only
// (D-14), and the server sanitizer discards them from any merchant write regardless.
export type ResolvedBranchCandidate = {
  link: string;
  confidence: "direct" | "matched" | "failed";
  reason?: string;
  distanceMeters?: number;
  branch?: {
    name: string;
    googlePlaceId: string;
    address?: string;
    governorate?: string;
    phone?: string;
    workingHours?: string[];
    geometry?: { lat: number; lng: number };
    mapsUrl?: string;
  };
};

/**
 * POST /api/merchant/store/branches/resolve — resolve exactly ONE pasted Google Maps link to a
 * branch candidate. The server rejects any other array length with a 400 (bulk paste is the
 * admin surface, T-32-06), and it authorizes off req.merchantClaim — so no storeId is sent here
 * in the body or the params (T-32-09).
 */
export const resolveBranchLink = async (
  link: string
): Promise<BackendApiResponse<ResolvedBranchCandidate>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/store/branches/resolve`,
      { links: [link] },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر التعرف على هذا الرابط. حاول مرة أخرى." };
  }
};

/**
 * GET /api/claims/mine — the caller's own claims. NOTE the BASE-less path: this is the
 * public claim endpoint (behind `auth`, not merchantAuth), used by MerchantRoute to gate the
 * portal on an approved claim (D-06).
 */
export const getMyClaims = async (): Promise<BackendApiResponse<MyClaim[]>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/claims/mine`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "تعذّر التحقق من حالة طلبك." };
  }
};
